import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';
import { isTesting } from '#constants/environment';
import { webviewManager } from '#webview/index';
import { getSharedCookieDomain } from '../utils/shared-cookie-domain';

declare global {
  interface Window {
    __TEST_DEVICE_MOCK__?: Partial<CurrentDevice>;
  }
}

const COOKIE_MAX_AGE_DAYS = 366; // 12 months in days (js-cookie uses days)

const ENV = getEnvironmentVariables();

const environment = ENV.environment;
const deployTarget = ENV.deployTarget;

const cookieDomain = getSharedCookieDomain(
  environment,
  deployTarget,
  typeof window !== 'undefined' ? window.location.href : ''
);

// Parse user agent once at module level (singleton pattern)
const parser = new UAParser();
const device = parser.getDevice();
const browser = parser.getBrowser();
const os = parser.getOS();

// Pre-compute derived values
const currentDevice = device.type;
const currentBrowserName = browser.name;
const isMobile = currentDevice === 'mobile';
const isSafariDesktop = currentBrowserName === 'Safari';
const isAndroid = os.name === 'Android';

// Get or create device ID from cookie (singleton pattern)
export function getOrCreateDeviceId(): string {
  let id = Cookies.get('deviceId');

  if (!id) {
    id = crypto.randomUUID();
    const isSecure = environment === 'production';
    Cookies.set('deviceId', id, {
      secure: isSecure,
      domain: cookieDomain || undefined,
      expires: COOKIE_MAX_AGE_DAYS,
      path: '/',
    });
  }

  return id;
}

const deviceId = getOrCreateDeviceId();

export interface CurrentDevice {
  id: string;
  currentDevice: string | undefined;
  currentBrowserName: string | undefined;
  isMobile: boolean;
  isSafariDesktop: boolean;
  os: UAParser.IOS;
  isAndroid: boolean;
  isMobileBrowser: boolean;
}

export function useCurrentDevice(): CurrentDevice {
  const baseDevice: CurrentDevice = {
    id: deviceId,
    currentDevice,
    currentBrowserName,
    isMobile,
    isMobileBrowser: isMobile && !webviewManager.isWebview,
    isSafariDesktop,
    os,
    isAndroid,
  };

  // In test environment, check for test mock
  if (isTesting && window.__TEST_DEVICE_MOCK__) {
    return {
      ...baseDevice,
      ...window.__TEST_DEVICE_MOCK__,
    };
  }

  return baseDevice;
}
